import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';
import StatisticRow from 'src/components/cardLayouts/StatisticRow/StatisticRow';
import { HeaderItemProps } from 'src/components/ui/HeaderItem/HeaderItem';
import { Kpi, KpiModel, useKpiQuery } from 'src/services/kpi';
import { formatAmount } from 'src/utils/general';
import { localeNumber } from 'src/localization/locale';
import { useProfile } from 'src/utils/ProfileService';

function formatNumber(num: number): string {
  return localeNumber(num, { maximumFractionDigits: 2 });
}
// KPIs whose value the backend delivers as a raw number that the frontend
// must decorate: a currency amount (€) or a percentage (%). Keyed off the
// stable `key` the backend now sends, not the (localized) display title.
const CURRENCY_KPI_KEYS = new Set(['total_inventory_value', 'inventory_value']);
const PERCENT_KPI_KEYS = new Set(['return_rate_30_days', 'return_rate_60_days']);

export function convertKpiToHeaderItemProps(kpiData: any, t?: TFunction): HeaderItemProps[] {
  return kpiData.map((item: any) => {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { value, title, kpi_type, progress_color } = item;
    const progressValue = 'progress' in item ? parseFloat(item.progress?.replace('%', '')) : NaN;
    const progressNumber = Number.isNaN(progressValue) ? undefined : progressValue;

    let keyValues: HeaderItemProps['keyValues'];

    if (kpi_type === 'key_value_list') {
      keyValues = Object.entries(item.value).map(([key, value]) => ({
        key: <span>{key}</span>,
        value: <span>{value as any}</span>,
      }));
    } else if (kpi_type === 'list') {
      keyValues = item.value.map((key: any) => ({
        key: <span>{key}</span>,
        value: <span />,
      }));
    } else {
      keyValues = undefined;
    }
    const res: HeaderItemProps = {
      title,
      kpiNumber: typeof value === 'number' ? formatNumber(value) : undefined,
      percentage: progressNumber,
      progressColor: progress_color || '',
      keyValues,
      description: t && item.time_span ? t('dashboard.last_30_days') : undefined,
    };
    return res;
  });
}

export function KpiRow({ model }: { model: KpiModel }) {
  const { data, isLoading } = useKpiQuery(model);
  const kpiData = data as Kpi[] | undefined;
  const { t } = useTranslation();
  const { profile, loading: profileLoading } = useProfile();
  const lang = profileLoading ? null : profile.lang;
  const resolvedKpis = kpiData ? convertKpiToHeaderItemProps(kpiData, t) : [];

  resolvedKpis.forEach((kpi, index) => {
    const key = (kpiData?.[index] as { key?: string })?.key;
    if (key && PERCENT_KPI_KEYS.has(key)) {
      kpi.kpiNumber = `${kpi.kpiNumber}%`;
    }
    if (key && CURRENCY_KPI_KEYS.has(key)) {
      const rawValue = kpiData?.[index]?.value;
      kpi.kpiNumber = typeof rawValue === 'number' ? `${formatAmount(rawValue, lang)}€` : kpi.kpiNumber;
    }
  });

  if (!isLoading && (!kpiData || kpiData.length === 0)) {
    return null;
  }

  return <StatisticRow loading={isLoading} items={resolvedKpis} />;
}
